import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const InteractiveTimeline = ({ timeline }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState(null);

  return (
    <section className="py-20 bg-gradient-to-br from-neutral-50 to-neutral-100 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-72 h-72 bg-violet-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-72 h-72 bg-indigo-200/20 rounded-full blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">
              Professional Journey
            </span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8">
            11+ years of experience building exceptional digital solutions
          </p>

          {/* Interactive Year Navigation - Hidden on mobile, visible on desktop */}
          <div className="hidden lg:flex flex-wrap justify-center gap-3 mb-12">
            {timeline.map((item, index) => (
              <motion.button
                key={index}
                onClick={() => {
                  setActiveIndex(index);
                  setExpandedIndex(null);
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 ${
                  activeIndex === index
                    ? 'bg-gradient-to-r from-violet-500 to-indigo-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm'
                }`}
              >
                {item.year}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Main Timeline Display */}
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left: Timeline Visual - Hidden on mobile */}
          <div className="hidden lg:block relative lg:sticky lg:top-32">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -20 }}
                transition={{ duration: 0.5 }}
                className="relative"
              >
                {/* Featured Card */}
                <div className="bg-white rounded-3xl p-8 shadow-2xl relative overflow-hidden group">
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative z-10">
                    {/* Company Icon */}
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center mb-6 shadow-lg">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>

                    {/* Year Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-100 to-indigo-100 rounded-full mb-4">
                      <svg className="w-4 h-4 text-violet-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      <span className="text-violet-600 font-bold text-sm">{timeline[activeIndex].year}</span>
                    </div>

                    <h3 className="text-3xl font-bold text-gray-900 mb-3">
                      {timeline[activeIndex].title}
                    </h3>

                    <p className="text-xl text-violet-600 font-semibold mb-6">
                      {timeline[activeIndex].role}
                    </p>

                    <p className="text-gray-600 leading-relaxed text-lg mb-6 whitespace-pre-line">
                      {timeline[activeIndex].description}
                    </p>

                    {/* Progress indicator */}
                    <div className="flex items-center gap-2">
                      {timeline.map((_, idx) => (
                        <div
                          key={idx}
                          className={`h-1 rounded-full transition-all duration-300 ${
                            idx === activeIndex ? 'bg-gradient-to-r from-violet-500 to-indigo-500 w-12' : 'bg-gray-300 w-8'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Navigation Arrows */}
                <div className="flex justify-between mt-6">
                  <motion.button
                    whileHover={{ scale: 1.05, x: -5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setActiveIndex(Math.max(0, activeIndex - 1))}
                    disabled={activeIndex === 0}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                      activeIndex === 0
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-violet-500 hover:to-indigo-500 hover:text-white shadow-md'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setActiveIndex(Math.min(timeline.length - 1, activeIndex + 1))}
                    disabled={activeIndex === timeline.length - 1}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                      activeIndex === timeline.length - 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-violet-500 hover:to-indigo-500 hover:text-white shadow-md'
                    }`}
                  >
                    Next
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right: All Positions List */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">All Positions</h3>
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                onClick={() => {
                  setActiveIndex(index);
                  // On mobile, toggle expansion
                  if (window.innerWidth < 1024) {
                    setExpandedIndex(expandedIndex === index ? null : index);
                  }
                }}
                className={`cursor-pointer rounded-2xl transition-all duration-300 ${
                  activeIndex === index
                    ? 'bg-gradient-to-r from-violet-500 to-indigo-500 shadow-xl'
                    : 'bg-white hover:bg-gray-50 shadow-md hover:shadow-lg'
                }`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                          activeIndex === index
                            ? 'bg-white/20 text-white'
                            : 'bg-violet-100 text-violet-600'
                        }`}>
                          {item.year}
                        </span>
                      </div>
                      <h4 className={`text-xl font-bold mb-1 ${
                        activeIndex === index ? 'text-white' : 'text-gray-900'
                      }`}>
                        {item.title}
                      </h4>
                      <p className={`text-sm font-medium ${
                        activeIndex === index ? 'text-white/90' : 'text-violet-600'
                      }`}>
                        {item.role}
                      </p>
                    </div>

                    {/* Arrow only visible on mobile */}
                    <motion.div
                      animate={{ rotate: expandedIndex === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="lg:hidden"
                    >
                      <svg
                        className={`w-6 h-6 ${activeIndex === index ? 'text-white' : 'text-gray-400'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.div>
                  </div>

                  {/* Expandable content - only on mobile */}
                  <AnimatePresence>
                    {expandedIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden lg:hidden"
                      >
                        <p className={`mt-4 pt-4 border-t leading-relaxed whitespace-pre-line ${
                          activeIndex === index
                            ? 'text-white/90 border-white/20'
                            : 'text-gray-600 border-gray-200'
                        }`}>
                          {item.description}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveTimeline;
